// The tour in moments (owner, 2026-10-04: "Rather than play the full tutorial up front, play the
// relevant segments of it as things occur naturally, as in when someone opens a task for the first
// time, explain how it works, when someone completes a task, explain gold and rewards, etc.").
//
// Each screen says when its moment happens (`playMoment`); this module decides which cards that
// moment still has to say on this device (shared/tours.ts stepsForMoment), whether a tour may open
// now (momentMayOpen), and keeps the one Tutorial the app shell draws (`useTour`). Only one tour
// shows at a time: a moment asked for while one shows is dropped, not queued. The question mark in
// Profile plays the whole tour (`playWholeTour`) and marks nothing seen.
import { useEffect, useState } from "preact/hooks";
import type { FlagKey } from "../shared/types";
import { FLAG_KEYS } from "../shared/flags.ts";
import { isPinned } from "../shared/nextUp.ts";
import { TOUR_STEPS, resolveSteps, type TourBoard, type TourMoment, type TourStep } from "../shared/tour-copy.ts";
import { momentMayOpen, stepKey, stepsForMoment } from "../shared/tours.ts";
import { currentFlags } from "./flags-core.ts";
import { selectorFor } from "./tour-targets.ts";
import { markTourSeen, tourSeen } from "./tutorial-state.ts";

/** The tour the app shell is drawing. */
export interface TourShow {
  /** Changes with every tour, so the Tutorial starts afresh. */
  id: number;
  /** The cards, already resolved against the screen (shared/tour-copy.ts resolveSteps). */
  steps: TourStep[];
  /** The moment playing, or null for the whole tour from Profile's question mark. */
  moment: TourMoment | null;
  /** The whole tour only: the open task its task cards show; null with none. */
  taskId: string | null;
}

/**
 * On the web a moment's card may point somewhere else than in the whole tour, because the moment
 * happens on another screen: "That's you" plays when the Profile sheet opens, so it spotlights the
 * Notifications row in the sheet rather than the Profile button under it.
 */
export const MOMENT_TARGETS: Partial<Record<TourMoment, Record<string, string>>> = {
  "profile-opened": { who: "notifications" },
};

/** What a moment needs to know to choose its cards. Injected in tests. */
export interface MomentEnv {
  /** The features on, in FLAG_KEYS order. */
  enabled: readonly FlagKey[];
  /** The cards this device has been shown (src/tutorial-state.ts). */
  seen: readonly string[];
  /** Whether a tour target is on the screen now. */
  present: (target: string) => boolean;
}

/**
 * The cards a moment plays now, or none: the moment's unseen cards whose feature is on, pointed at
 * this screen's targets, resolved against what the screen holds, and only those whose target is
 * on the screen (a card with no target always is).
 */
export function momentSteps(moment: TourMoment, env: MomentEnv, showing: boolean, toured: boolean): TourStep[] {
  if (!momentMayOpen(moment, showing, toured)) return [];
  const remap = MOMENT_TARGETS[moment] ?? {};
  const steps = stepsForMoment(moment, env.enabled, env.seen).map((step) =>
    step.target && remap[step.target] ? { ...step, target: remap[step.target] } : step,
  );
  if (steps.length === 0) return [];
  const board: TourBoard = {
    // A moment plays on its own screen, never somewhere it would have to open: a task card is
    // there when the task screen shows its Job's Done button.
    hasTask: env.present("job-done"),
    hasNextUp: env.present("next-up"),
    bountyUI: env.enabled.includes("bountyUI"),
  };
  return resolveSteps(steps, board).filter((step) => !step.target || env.present(step.target));
}

// ---------- the one tour ----------

let current: TourShow | null = null;
let nextId = 1;
/** A tour has shown since the page loaded (a "board" moment then waits for the next open). */
let touredThisOpen = false;
/** The first-open moment has been tried this page load. */
let firstOpenTried = false;
/** A job was just finished: the next open board plays "task-done". */
let doneWaiting = false;
const listeners = new Set<() => void>();

function emit(): void {
  for (const fn of listeners) fn();
}

function defaultEnv(): MomentEnv {
  const flags = currentFlags();
  return {
    enabled: FLAG_KEYS.filter((key) => flags[key]),
    seen: tourSeen(),
    present: (target) => {
      if (typeof document === "undefined") return false;
      const el = document.querySelector(selectorFor(target));
      return !!el && el.getClientRects().length > 0;
    },
  };
}

function open(show: Omit<TourShow, "id">): void {
  current = { ...show, id: nextId++ };
  touredThisOpen = true;
  emit();
}

/**
 * Plays a moment's cards if it has any left on this device and no tour is showing. Returns whether
 * a tour opened. A moment asked for while a tour shows is dropped; the screen asks again the next
 * time it happens (a "board" moment on the next board render).
 */
export function playMoment(moment: TourMoment, env: MomentEnv = defaultEnv()): boolean {
  const steps = momentSteps(moment, env, current !== null, touredThisOpen);
  if (steps.length === 0) return false;
  open({ steps, moment, taskId: null });
  return true;
}

/**
 * The open board has rendered: the first open's welcome once per page load, else a job just
 * finished, else whatever the board shows for the first time (Next up, the note button).
 */
export function playBoardMoments(env?: MomentEnv): void {
  if (current) return;
  if (!firstOpenTried) {
    firstOpenTried = true;
    const played = playMoment("first-open", env);
    if (!played) emit(); // firstOpenDue changed
    if (played) return;
  }
  if (doneWaiting && playMoment("task-done", env)) {
    doneWaiting = false;
    return;
  }
  playMoment("board", env);
}

/** A job was finished: say so on the board it returns to. */
export function jobFinished(): void {
  doneWaiting = true;
}

/** The open tasks, as the whole tour needs them to choose its task cards. */
export interface OpenTaskLite {
  id: string;
  schedule_id: string | null;
  urgent?: number | boolean | null;
  luxury?: number | null;
}

/**
 * The question mark in Profile: every card in tour order whose feature is on, walking from screen
 * to screen. `open` is the open tasks (null when they could not be read). Marks nothing seen.
 */
export function playWholeTour(openTasks: readonly OpenTaskLite[] | null, enabled: readonly FlagKey[] = defaultEnv().enabled): boolean {
  if (current) return false;
  const board: TourBoard = {
    hasTask: !!openTasks?.length,
    hasNextUp: !!openTasks?.some((t) => !!t.schedule_id || isPinned(t)),
    bountyUI: enabled.includes("bountyUI"),
  };
  const steps = resolveSteps(
    TOUR_STEPS.filter((step) => !step.feature || enabled.includes(step.feature)),
    board,
  );
  if (steps.length === 0) return false;
  open({ steps, moment: null, taskId: openTasks?.[0]?.id ?? null });
  return true;
}

/**
 * The showing tour's cards count as seen (its last card showed, or it was skipped): recorded at
 * once, so closing the app on the last card does not bring them back. The whole tour marks nothing.
 */
export function tourCardsShown(): void {
  if (current?.moment) markTourSeen(current.steps.map(stepKey));
}

/** The Tutorial closed (finished or skipped). */
export function closeTour(): void {
  if (!current) return;
  tourCardsShown();
  current = null;
  emit();
}

export function currentTour(): TourShow | null {
  return current;
}

/**
 * The first open's welcome is still to come this page load. The app holds its other first-run
 * layers (the install sheet, the notification prompt, the recap) until it has had its turn.
 */
export function firstOpenDue(env?: Pick<MomentEnv, "seen">): boolean {
  if (firstOpenTried) return false;
  return stepsForMoment("first-open", [], env?.seen ?? tourSeen()).length > 0;
}

/** The tour the shell draws, and whether the first open's welcome is still to come. */
export function useTour(): { show: TourShow | null; firstOpenDue: boolean } {
  const read = () => ({ show: current, firstOpenDue: firstOpenDue() });
  const [state, setState] = useState(read);
  useEffect(() => {
    const update = () => setState(read());
    listeners.add(update);
    update();
    return () => {
      listeners.delete(update);
    };
  }, []);
  return state;
}

/** Tests only. */
export function resetToursForTests(): void {
  current = null;
  touredThisOpen = false;
  firstOpenTried = false;
  doneWaiting = false;
  listeners.clear();
}
